import * as React from 'react'
import { Button, Card, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { aqIdExistsAPI } from 'api/aqId'
import { PaymentStatus, PositionNumber, PositionNumberType, castConstantType } from 'api/common/constants'
import { AtemQuestIdExistsRequest } from 'api/common/requests'
import { Status } from 'api/common/statuses'
import { AtemQuestId, PositionSimplified } from 'api/common/types'
import { positionContractAPI, positionsAPI } from 'api/position'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputField from 'components/InputField'
import PositionImage from 'images/position_image.svg'
import loginData from 'utilities/loginData'
import { ValidateResult, atemQuestIdValidator, emailValidator } from 'utilities/validators'
import { PositionInfo } from 'utilities/resources/formItemValues'

type StateValue = {
  fromPositionManagement?: boolean
}

const SceneComponent: React.FC = () => {
  type FormData = {
    positionNumber: PositionNumberType
    atemQuestId: string
    positionId: number
    email: string
    invalid: boolean
  }
  type FormMessages = {
    atemQuestId: string[]
    email: string[]
  }

  const navigate = useNavigate()
  const location = useLocation()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const { fromPositionManagement } = stateValue
  const [showPositionTree, setShowPositionTree] = React.useState(false)
  const [formData, setFormData] = React.useState<FormData[]>([])
  const [formMessages, setFormMessages] = React.useState<FormMessages[]>([])
  const [errorMessage, setErrorMessage] = React.useState<string>('')
  const [isSpinningSubmit, setIsSpinningSubmit] = React.useState(false)
  const [subPositionCount, setSubPositionCount] = React.useState(0)
  const [inputCount, setInputCount] = React.useState(0)
  const prevScene = fromPositionManagement ? '/positionManagement' : '/positionSelect'

  const handleAtemQuestId = (index: number, value: string) => {
    let _inputCount = inputCount
    if (formData[index].positionNumber !== PositionNumber.MainPosition) {
      if (!!formData[index].atemQuestId && !value && !formData[index].email) {
        // 元が入力されていたかつ、空文字が指定されたかつ、メールアドレスが入力されていなければカウントを減らす
        _inputCount--
      }
      if (!formData[index].atemQuestId && !!value && !formData[index].email) {
        // 元が空だったかつ、文字が入力されたかつ、メールアドレスも空だった場合はカウントを増やす
        _inputCount++
      }
    }
    formData[index].atemQuestId = value
    updateInputForm(_inputCount, formData)
  }
  const handleEmail = (index: number, value: string) => {
    let _inputCount = inputCount
    if (!!formData[index].email && !value && !formData[index].atemQuestId) {
      // 元が入力されていたかつ、空文字が指定されたかつ、ATEMクエストIDが入力されていなければカウントを減らす
      _inputCount--
    }
    if (!formData[index].email && !!value && !formData[index].atemQuestId) {
      // 元が空だったかつ、文字が入力されたかつ、ATEMクエストIDも空だった場合はカウントを増やす
      _inputCount++
    }
    formData[index].email = value
    updateInputForm(_inputCount, formData)
  }
  const updateInputForm = (count: number, data: FormData[]) => {
    data.forEach((d) => {
      if (d.positionNumber !== PositionNumber.MainPosition) {
        if (!d.atemQuestId && !d.email) {
          d.invalid = count >= subPositionCount
        }
      }
    })
    setFormData([...data])
    setInputCount(count)
    console.log(count)
  }
  const validation = async (): Promise<boolean> => {
    let isValidAll = true
    const newFormMessages: FormMessages[] = []
    for (let i = 0; i < formData.length; i++) {
      const { atemQuestId, email, invalid } = formData[i]
      const message: FormMessages = {
        atemQuestId: [],
        email: [],
      }
      newFormMessages.push(message)

      if (invalid) continue
      const emailValidateResult = emailValidator(email)
      switch (emailValidateResult) {
        case ValidateResult.Empty:
          message.email.push('メールアドレスが空白です')
          isValidAll = false
          break
        case ValidateResult.Invalid:
          message.email.push('メールアドレスの形式が正しくありません')
          isValidAll = false
          break
      }

      const atemQuestIdValidateResult = atemQuestIdValidator(atemQuestId)
      switch (atemQuestIdValidateResult) {
        case ValidateResult.Empty:
          message.atemQuestId.push('AUBEクエストIDが空白です')
          isValidAll = false
          break
      }
    }

    if (isValidAll) {
      // ATEMクエストID存在チェック
      const requestParam: AtemQuestIdExistsRequest = {
        atemQuestIds: formData.map<AtemQuestId>((data) => ({ atemQuestId: data.atemQuestId, email: data.email })),
      }
      await aqIdExistsAPI(requestParam, (response) => {
        response.exists.forEach((exists, index) => {
          if (!exists) {
            newFormMessages[index].atemQuestId.push('AUBEクエストIDが存在しません')
            isValidAll = false
          }
        })
      })
    }

    setFormMessages(newFormMessages)
    setErrorMessage('')
    return isValidAll
  }

  /**
   * 「お申込みへ」ボタン
   */
  const onSubmit = async (): Promise<void> => {
    setIsSpinningSubmit(true)
    if (await validation()) {
      const contractPositions: PositionSimplified[] = formData
        .filter((data) => !data.invalid)
        .map<PositionSimplified>((formData) => ({
          positionNumber: formData.positionNumber,
          atemQuestId: formData.atemQuestId,
          email: formData.email,
        }))
      const showPositions: PositionInfo[] = formData
        .filter((data) => !data.invalid)
        .map<PositionInfo>((formData) => ({
          positionNumber: formData.positionNumber,
          atemQuestId: formData.atemQuestId,
          positionId: formData.positionId,
        }))
      await positionContractAPI({ positions: contractPositions }, (status, result) => {
        if (status === Status.Success && result) {
          navigate('/aubeQuestDealPurchaseForBankTransfer', { state: { contractPositions, showPositions } })
        } else if (status !== Status.Success) {
          setErrorMessage('AUBEクエストIDが重複しています。')
        }
      })
    }
    setIsSpinningSubmit(false)
  }

  /**
   * 「ポジション選択をやり直す」ボタン
   */
  const onCancel = (): void => {
    loginData.resetPurchaseData()
    navigate(prevScene)
  }

  React.useEffect(() => {
    positionsAPI((result) => {
      // 契約可能なPositionNumberのリスト
      const contractablePositionNumbers: PositionNumberType[] = []

      // ポジションの枠を算出
      const positionCount = Math.max(result.maxPositionCount, result.positions.length)
      for (let i = 0; i < positionCount; i++) {
        const positionNumber = castConstantType<PositionNumberType>(i + 1, PositionNumber)
        if (!positionNumber) continue

        const position = result.positions.find((position) => position.positionNumber === positionNumber)
        if (!position || (!position.isChangedAssignment && position.paymentStatus !== PaymentStatus.Paid)) {
          // 契約可能なPositionNumber
          contractablePositionNumbers.push(positionNumber)
        }
      }

      // 契約を行うPositionNumberのリスト
      const targetPositionNumbers: PositionNumberType[] = []

      const positionContractData = loginData.getPositionContractSelectedData()
      if (!positionContractData) {
        navigate(prevScene)
        return
      } else if (positionContractData.selectedPositionNumber) {
        // ポジションが指定されていた場合
        if (contractablePositionNumbers.includes(positionContractData.selectedPositionNumber)) {
          // 契約可能
          targetPositionNumbers.push(positionContractData.selectedPositionNumber)
        } else {
          // 契約不可能なPositionNumber
          navigate(prevScene)
          return
        }
      } else {
        // ポジションが指定されていない場合
        if (positionContractData.additionPositionCount > contractablePositionNumbers.length) {
          // 契約可能数より多い
          navigate(prevScene)
          return
        } else {
          let positionFrameCount = positionContractData.additionPositionCount
          if (loginData.getRegistrationStatus()) {
            setShowPositionTree(true)
            // 事前登録ユーザーは枠分表示する
            positionFrameCount = contractablePositionNumbers.length
          }
          // 契約したいポジションの個数分回す
          for (let i = 0; i < positionFrameCount; i++) {
            targetPositionNumbers.push(contractablePositionNumbers[i])
          }
        }
      }

      // 契約可能なサブポジションの個数
      let contractableSubPositionCount = positionContractData.additionPositionCount
      if (contractablePositionNumbers.includes(PositionNumber.MainPosition)) {
        contractableSubPositionCount--
      }
      setSubPositionCount(contractableSubPositionCount)

      let count = 0
      const data: FormData[] = []
      const messages: FormMessages[] = []
      // 契約を行うポジションのデータを作成
      targetPositionNumbers.forEach((positionNumber) => {
        const position = result.positions.find((position) => position.positionNumber === positionNumber)
        const atemQuestId = position?.atemQuestId ?? ''
        const positionId = position?.positionId ?? 0
        const email = position?.atemQuestEmail ?? ''
        data.push({
          positionNumber,
          atemQuestId,
          positionId,
          email,
          invalid: false,
        })
        messages.push({
          atemQuestId: [],
          email: [],
        })
        if (positionNumber !== PositionNumber.MainPosition) {
          count += !!atemQuestId || !!email ? 1 : 0
        }
      })
      if (count >= subPositionCount) {
        // すべてのサブポジションに入力されている
        for (const d of data) {
          if (d.positionNumber !== PositionNumber.MainPosition && !d.atemQuestId && !d.email) d.invalid = true
        }
      }
      setInputCount(count)
      setFormData(data)
      setFormMessages(messages)
    })
  }, [navigate, subPositionCount, prevScene])
  return (
    <>
      <Header title="ポジション契約" />
      <main>
        <section className="mx-4 my-5">
          <p className="note">
            AUBEクエストディール配布先のAUBEクエストIDをご入力下さい。
            <br />
            <span className="note-warning">ここで入力されたAUBEクエストID（ゲームのID）とコネクターIDが連結され報酬計算の対象となります。</span>
            <br />
            報酬取得権利確定課金の計算対象はこのポジション及び、このポジションから始まるパーソナルグループとなります。
            <br />
            サブポジションはポジション管理画面より、個別で決済する事も可能です。
          </p>
          <Card body className="border-0 text-center" hidden={!showPositionTree}>
            <img src={PositionImage} alt="ポジションイメージ" width={'100%'} />
          </Card>
          {formData.map(({ positionNumber, atemQuestId, email, invalid }, index) => (
            <div key={index} className="mt-4">
              <div>
                <span className="font-15 fw-bold">{positionNumber === PositionNumber.MainPosition ? 'メインポジション' : `サブポジション${positionNumber - 1}`}</span>
                {positionNumber === PositionNumber.MainPosition && <span className="note ms-2">※必ず決済してください</span>}
              </div>
              <InputField
                name="atem-quest-id"
                type="text"
                value={atemQuestId}
                onChange={(e) => handleAtemQuestId(index, e.target.value)}
                placeholder="AUBEクエストID"
                aria-label="AUBEクエストID"
                disabled={invalid}
                errors={formMessages[index].atemQuestId}
                bold
              />
              <InputField
                name="atem-quest-email"
                type="email"
                value={email}
                onChange={(e) => handleEmail(index, e.target.value)}
                className="mt-2"
                placeholder="AUBEクエストに登録したメールアドレス"
                aria-label="AUBEクエストに登録したメールアドレス"
                disabled={invalid}
                errors={formMessages[index].email}
                bold
              />
            </div>
          ))}
          <div className="text-danger" hidden={errorMessage === ''}>
            {errorMessage}
          </div>
          <div className="mt-5">
            <Button variant="primary" className="w-100 py-4" disabled={isSpinningSubmit} onClick={onSubmit}>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isSpinningSubmit} />
              お申込みへ
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Button variant="link" onClick={onCancel}>
              ポジション数選択をやり直す
            </Button>
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
